'use client';

import Link from 'next/link';
import { ReactNode, useRef, useState } from 'react';

import { usePathname, useRouter } from '@flipster/i18n/routing';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { createTabsComponent } from '@flipster/react/design-system/tabs';
import { useIntersection } from '@flipster/react/misc/browser/intersection';
import { useThrottleCallback } from '@flipster/react/misc/throttle';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { isServer } from '@flipster/utils/env-helper';

import { BlogCategoryFragment, BlogPostSummaryFragment } from '@web/app/[locale]/(web)/blog/query.generated';
import blogEmptyImg from '@web/assets/images/blog-page/blog-empty.webp';

import BlogDateTime from '../_component/BlogDateTime';

const getHashPage = (): number => {
  if (isServer) return 1;
  const [, page] = window.location.hash.split('#');
  return Number(page || 1);
};

const Tabs = createTabsComponent<string>();

interface Props {
  title: ReactNode;
  categories: BlogCategoryFragment[];
  posts: BlogPostSummaryFragment[];
}

const BlogList = ({ title, categories, posts }: Props) => {
  const per = 9;

  const router = useRouter();
  const pathname = usePathname();
  const ref = useRef<HTMLDivElement>(null);
  const initRef = useRef<boolean>(false);

  const [page, setPage] = useState<number>(getHashPage());

  const isLast = posts?.length < page * per;
  const currentPosts = posts?.slice(0, page * per);
  const categoryTabList = [
    { label: 'All', value: '/blog' },
    ...categories.map((ct) => ({ label: ct.name!, value: `/blog/category/${ct.slug}` })),
  ];

  const onThrottleScroll = useThrottleCallback(() => {
    if (isLast) return;
    // 초기 렌더링시 +1 막기
    if (!initRef.current) {
      initRef.current = true;
      return;
    }
    const hashPage = getHashPage();
    if (page < hashPage) {
      setPage(hashPage);
      return;
    }
    setPage((prevState) => {
      const next = prevState + 1;
      window.history.replaceState(undefined, '', `#${next}`);
      return next;
    });
  }, 100);

  useIntersection(ref, onThrottleScroll);

  return (
    <div className={css({ pb: 84, m: '24px 20px', smallDesktop: { maxWidth: 1200, m: '80px auto' } })}>
      <header>
        <h1 className={css({ textStyle: 'Display/XL', color: 'inverse.white', mb: '1em' })}>{title}</h1>
      </header>
      <Tabs type='scrollable' value={pathname} css={{ mb: 32 }} onClick={(page) => router.push(page)}>
        {categoryTabList.map((tab) => (
          <Tabs.Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>
      <ul
        className={css({
          display: 'grid',
          gap: '64px 24px',
          pb: 24,
          gridTemplateColumns: '1fr',
          tablet: { gridTemplateColumns: 'repeat(2, 1fr)' },
          smallDesktop: { gridTemplateColumns: 'repeat(3, 1fr)' },
        })}
      >
        {currentPosts.map((post) => {
          const postURL = `/blog/${post.slug}`;

          return (
            <li
              className={css({ _hover: { '& .blog-thumbnail': { transform: 'scale(1.3)' } } })}
              key={`post-${post.slug}`}
            >
              <Link href={postURL}>
                <div className={css({ mb: 24, borderRadius: 6, overflow: 'hidden' })}>
                  <div
                    style={{
                      backgroundImage: `url(${post.featuredImage?.url ? `${post.featuredImage.url}?fm=webp&w=600` : blogEmptyImg.src})`,
                    }}
                    className={`blog-thumbnail ${css({
                      pb: '57%',
                      backgroundPosition: '50%',
                      backgroundSize: 'cover',
                      transition: 'transform 0.8s cubic-bezier(0.075, 0.82, 0.165, 1)',
                      transform: 'scale(1)',
                    })}`}
                  />
                </div>
              </Link>

              <div className={flex({ flexFlow: 'column', gap: 12 })}>
                <div>
                  {post.categoriesCollection?.items.map((category) => (
                    <span
                      key={`post-${post.slug}-category-${category?.slug}`}
                      className={css({
                        textStyle: 'Text/S/Strong',
                        textTransform: 'uppercase',
                        color: 'content.accent.info',
                        mr: 4,
                      })}
                    >
                      {category?.name}
                    </span>
                  ))}
                </div>
                <h3>
                  <Link href={postURL} className={css({ textStyle: 'Title/M', color: 'inverse.white' })}>
                    {post.title}
                  </Link>
                </h3>
                <div className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
                  {post.summary}
                </div>
                <BlogDateTime
                  dateString={post.displayPublishedAt || post.sys.firstPublishedAt}
                  className={css({
                    textStyle: 'Text/S/Strong',
                    textTransform: 'uppercase',
                    color: 'content.default.level-2',
                  })}
                />
              </div>
            </li>
          );
        })}
      </ul>
      {!isLast && (
        <div className={css({ width: '100%' })} ref={ref}>
          <SpinnerBox />
        </div>
      )}
    </div>
  );
};

export default BlogList;
